import React, { useState, useEffect, useRef } from 'react';
import productData from '../../data/product.json';
import {
  Overlay,
  Content,
  CloseButton,
  VideoSection,
  VideoPlayer,
  Video,
  RecommendedSection,
  RecommendedGrid,
  VideoCard,
  VideoPoster,
  VideoTitle,
  VideoDescription,
  Sidebar,
  SidebarTitle,
  AdCard,
  AdImage,
  AdInfo,
  AdTitle,
  AdDescription,
  AdPrice,
  AdLink,
  AdQRColumn,
  AdQR,
  Spinner,
} from './styles/short-video-popup';

export default function ShortVideoPopup({ isOpen, onClose, selectedVideo, relatedVideos, ads, onVideoClick }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSceneId, setSelectedSceneId] = useState(null);
  const videoRef = useRef(null);

  // Hàm generate QR code từ URL
  const generateQRCode = (url) => {
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(url)}`;
    return qrApiUrl;
  };

  // Set scene đầu tiên làm mặc định khi mở popup
  useEffect(() => {
    if (isOpen && selectedVideo?.scenes && selectedVideo.scenes.length > 0) {
      setSelectedSceneId(selectedVideo.scenes[0].id);
    } else {
      setSelectedSceneId(null);
    }
  }, [isOpen, selectedVideo]);

  // Nhảy đến timestamp của scene được chọn
  useEffect(() => {
    if (!videoRef.current || !selectedSceneId || !selectedVideo?.scenes) return;

    const selectedScene = selectedVideo.scenes.find((scene) => scene.id === selectedSceneId);
    if (!selectedScene || typeof selectedScene.timestamp !== 'number') return;

    const video = videoRef.current;

    // Nếu video đã sẵn sàng, nhảy ngay
    if (video.readyState >= 2) {
      video.currentTime = selectedScene.timestamp;
    } else {
      // Nếu chưa sẵn sàng, đợi metadata load xong
      const handleLoadedMetadata = () => {
        video.currentTime = selectedScene.timestamp;
      };
      video.addEventListener('loadedmetadata', handleLoadedMetadata);

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [selectedSceneId, selectedVideo]);

  // Fetch products dựa trên scene được chọn
  useEffect(() => {
    if (!isOpen || !selectedSceneId) {
      setProducts([]);
      setError(null);
      return;
    }

    // Tìm scene được chọn để lấy timestamp
    const currentScene = selectedVideo?.scenes?.find((scene) => scene.id === selectedSceneId);
    if (!currentScene?.timestamp) {
      return;
    }

    const loadProducts = () => {
      setLoading(true);
      setError(null);

      try {
        // --- API call đã được comment lại, dùng local JSON thay thế ---
        // Call the API with image URL
        // const apiUrl = process.env.API_GET_CLOTHES || 'https://vieon.farmcode.io.vn/analyze/url';
        // const response = await fetch(apiUrl, {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({ image_url: currentScene.thumbnail, k_per_item: 2 }),
        // });
        // if (!response.ok) throw new Error('Failed to fetch products');
        // const data = await response.json();

        // Lookup từ product.json theo video slug + timestamp
        const videoEntry = productData.find((entry) => entry.video === selectedVideo?.slug);
        const matchedProducts = videoEntry
          ? videoEntry.product.filter((p) => p.timestamp === currentScene.timestamp)
          : [];

        const extractedProducts = matchedProducts.map((p) => ({
          id: p.sku,
          sku: p.sku,
          title: p.title,
          category: p.category,
          color: p.color,
          price: p.price,
          image: p.image_url,
          product_url: p.product_url,
          slot: p.slot,
        }));

        setProducts(extractedProducts);
      } catch (err) {
        console.error('Error getting products:', err);
        setError('Không thể tải sản phẩm');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [isOpen, selectedSceneId, selectedVideo]);

  if (!isOpen || !selectedVideo) return null;

  console.log('ShortVideoPopup - selectedVideo:', selectedVideo);
  console.log('ShortVideoPopup - scenes:', selectedVideo?.scenes);

  // Handler khi click vào scene
  const handleSceneClick = (scene) => {
    setSelectedSceneId(scene.id);
    // Không cần gọi onVideoClick vì chúng ta chỉ muốn thay đổi scene được chọn
    // để detect sản phẩm, không thay đổi toàn bộ selectedVideo
  };

  return (
    <Overlay onClick={onClose}>
      <Content onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>✕</CloseButton>

        <VideoSection>
          <VideoPlayer>
            <Video ref={videoRef} controls autoPlay>
              <source src={selectedVideo.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </Video>
          </VideoPlayer>

          <RecommendedSection>
            <RecommendedGrid>
              {selectedVideo?.scenes && selectedVideo.scenes.length > 0 ? (
                selectedVideo.scenes.map((scene) => (
                  <VideoCard
                    key={scene.id}
                    onClick={() => handleSceneClick(scene)}
                    style={{
                      border: selectedSceneId === scene.id ? '2px solid #00ff00' : '2px solid transparent',
                      boxShadow: selectedSceneId === scene.id ? '0 0 10px rgba(0, 255, 0, 0.5)' : 'none',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <VideoPoster src={scene.thumbnail} alt={scene.title} />
                    <VideoTitle
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {scene.title}
                    </VideoTitle>
                    <VideoDescription
                      style={{
                        fontSize: '11px',
                      }}
                    >
                      {scene.description}
                    </VideoDescription>
                    <div
                      style={{
                        color: '#00ff00',
                        fontSize: '11px',
                        marginTop: '4px',
                        paddingLeft: '8px',
                        paddingBottom: '8px',
                        fontWeight: '500',
                      }}
                    >
                      {scene.timestampFormatted}
                    </div>
                  </VideoCard>
                ))
              ) : (
                <div
                  style={{
                    color: 'white',
                    padding: '20px',
                    textAlign: 'center',
                  }}
                >
                  Không có scene nào trong highlight này
                </div>
              )}
            </RecommendedGrid>
          </RecommendedSection>
        </VideoSection>

        <Sidebar>
          <SidebarTitle>Sản phẩm liên quan</SidebarTitle>

          {loading && (
            <div
              style={{
                color: 'white',
                textAlign: 'center',
                padding: '20px',
                fontSize: '14px',
              }}
            >
              <Spinner />
              Đang phân tích trang phục...
            </div>
          )}

          {error && (
            <div
              style={{
                color: '#ff6b6b',
                textAlign: 'center',
                padding: '20px',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && products.length === 0 && (
            <div
              style={{
                color: '#999',
                textAlign: 'center',
                padding: '20px',
                fontSize: '14px',
              }}
            >
              Không tìm thấy sản phẩm phù hợp
            </div>
          )}

          {!loading &&
            products.map((product) => (
              <AdCard key={product.id}>
                <AdImage src={product.image} alt={product.title} />
                <AdInfo>
                  <AdTitle
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: '1.4',
                    }}
                  >
                    {product.title}
                  </AdTitle>
                  <AdPrice>
                    {new Intl.NumberFormat('vi-VN', {
                      style: 'currency',
                      currency: 'VND',
                    }).format(product.price)}
                  </AdPrice>
                  <AdLink href={product.product_url} target="_blank" rel="noopener noreferrer">
                    Xem sản phẩm
                  </AdLink>
                </AdInfo>
                <AdQRColumn>
                  <AdQR src={generateQRCode(product.product_url)} alt="QR Code" />
                </AdQRColumn>
              </AdCard>
            ))}
        </Sidebar>
      </Content>
    </Overlay>
  );
}
